/**
 * Labour time of a work order's job lines ("Työaika yhteensä" in the Jobs card).
 *
 * Shown so the workshop can book the calendar without adding the lines up by hand:
 *   - hourly job: quantity is the billed hours, so that is the time
 *   - fixed / variable job: the catalogue job's estimated_duration (minutes) per unit x quantity
 *
 * Not counted:
 *   - lines the customer declined (confirmation_status "denied"): the work will not be done
 *   - deduction lines (negative unit price, e.g. "Hyvitys") and non-positive quantities: not work
 *
 * A fixed / variable line without an estimated duration is reported in missingCount, so the
 * UI can say the total is incomplete instead of silently showing too little.
 */
import { formatQuantity } from '../utils';

/**
 * @param {Array} jobs - maintenance.jobs as serialized by MaintenanceJobDetailSerializer
 * @returns {{minutes: number, countedCount: number, missingCount: number}}
 */
export const calcJobsLaborTime = (jobs) => {
  let minutes = 0;
  let countedCount = 0;
  let missingCount = 0;

  (jobs || []).forEach((job) => {
    if (job.confirmation_status === 'denied') return;

    const quantity = parseFloat(job.quantity) || 0;
    if (quantity <= 0) return;
    if ((parseFloat(job.effective_price) || 0) < 0) return;

    if (job.price_type === 'hourly') {
      minutes += quantity * 60;
      countedCount += 1;
      return;
    }

    const estimatedMinutes = parseFloat(job.estimated_duration) || 0;
    if (estimatedMinutes > 0) {
      minutes += estimatedMinutes * quantity;
      countedCount += 1;
    } else {
      missingCount += 1;
    }
  });

  return { minutes, countedCount, missingCount };
};

/**
 * Minutes as decimal hours, the way the job lines show them: 90 -> "1,5", 50 -> "0,83".
 * @param {number} minutes
 * @returns {string}
 */
export const formatLaborHours = (minutes) => formatQuantity((minutes || 0) / 60);

/**
 * A catalogue job's estimated_duration (whole minutes, as stored) as decimal hours: 30 -> 0.5,
 * 50 -> 0.83. This is the hourly line's prefilled quantity, so it rounds to two decimals half up
 * like the other commercial amounts (verstaani/utils/vat.py). Integer maths, no float drift.
 * @param {number|string|null} minutes
 * @returns {number|null} null when the job has no estimate
 */
export const durationMinutesToHours = (minutes) => {
  const m = parseInt(minutes, 10);
  if (!Number.isFinite(m) || m <= 0) return null;
  return Math.floor((m * 100 + 30) / 60) / 100;
};

/**
 * Hours typed in a duration field ("1,5" or "1.5") as whole minutes for estimated_duration,
 * rounded to the nearest minute: "1,5" -> 90, "0,83" -> 50. Every stored minute value survives
 * the round trip through durationMinutesToHours.
 * @param {number|string|null} hours
 * @returns {number|null} null when empty or zero (no estimate)
 */
export const durationHoursToMinutes = (hours) => {
  if (hours === null || hours === undefined) return null;
  const h = parseFloat(String(hours).replace(',', '.'));
  if (!Number.isFinite(h) || h <= 0) return null;
  return Math.round(h * 60) || null;
};

/**
 * The estimate a fixed / variable line adds to the labor time total, shown on the line so the
 * workshop sees where the total comes from: the job's estimated_duration x quantity. Hourly
 * lines get null, their quantity already is their hours. Same exclusions as calcJobsLaborTime
 * for deductions and non-positive quantities.
 * @param {object} job - one of maintenance.jobs
 * @returns {number|null} minutes, or null when there is nothing to show
 */
export const lineEstimateMinutes = (job) => {
  if (!job || job.price_type === 'hourly') return null;
  const estimatedMinutes = parseFloat(job.estimated_duration) || 0;
  const quantity = parseFloat(job.quantity) || 0;
  if (estimatedMinutes <= 0 || quantity <= 0) return null;
  if ((parseFloat(job.effective_price) || 0) < 0) return null;
  return estimatedMinutes * quantity;
};
